'use client';

import { c as _c } from "react/compiler-runtime";
import { BoundingBox } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Image } from 'react-datocms/image';
import { ImageGalleryCarouselModal } from '~/components/ImageGallery';
import type { GalleryDatoImage, GalleryImage, ItineraryCardGalleryProps } from './ItineraryCard.types';
import styles from './ItineraryCardGallery.module.css';
const isDatoImage = (image: GalleryImage): image is GalleryDatoImage => 'data' in image;
const hasValidSrc = (image: GalleryImage) => isDatoImage(image) ? Boolean(image.data?.src) : Boolean(image.src);
function galleryImageKey(image: GalleryImage, index: number) {
  return `${isDatoImage(image) ? image.data.src : image.src}-${index}`;
}
const SLIDER_SIZES = '(min-width: 768px) 1px, 100vw';
const MAIN_IMAGE_SIZES = '(min-width: 1280px) 400px, (min-width: 768px) 33vw, 100vw';
const SUB_IMAGE_SIZES = '(min-width: 1280px) 200px, (min-width: 768px) 17vw, 50vw';
const renderGalleryImage = (image: GalleryImage, className: string, loading: 'eager' | 'lazy' = 'lazy') => isDatoImage(image) ? <Image data={image.data} className={className} layout="fill" objectFit="cover" sizes={SLIDER_SIZES} /> : <img src={image.src} srcSet={image.srcSet} sizes={image.srcSet ? SLIDER_SIZES : undefined} alt={image.alt ?? ''} className={className} loading={loading} />;
export function ItineraryCardGallery(t0) {
  const $ = _c(32);
  const {
    images,
    sliderOnly: t1,
    tag
  } = t0;
  const sliderOnly = t1 === undefined ? false : t1;
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  const [slideIndex, setSlideIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const scrollRef = useRef(null);
  let resolved;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== images || $[1] !== slideIndex || $[2] !== sliderOnly || $[3] !== t || $[4] !== tAria || $[5] !== tag) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      resolved = images?.filter(hasValidSrc) ?? [];
      if (resolved.length === 0) {
        t6 = null;
        break bb0;
      }
      const [mainImage, second, third] = resolved;
      let t7;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = () => {
          if (!scrollRef.current) {
            return;
          }
          const {
            scrollLeft,
            clientWidth
          } = scrollRef.current;
          if (clientWidth > 0) {
            setSlideIndex(Math.round(scrollLeft / clientWidth));
          }
        };
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      const handleScroll = t7;
      let t8;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = () => setIsOverlayOpen(true);
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      const openOverlay = t8;
      t2 = "ItineraryCardGallery";
      t3 = styles.gallery;
      let t9;
      if ($[14] !== tAria) {
        t9 = tAria("carousel");
        $[14] = tAria;
        $[15] = t9;
      } else {
        t9 = $[15];
      }
      let t10;
      if ($[16] !== t) {
        t10 = t("overview_trip_photos");
        $[16] = t;
        $[17] = t10;
      } else {
        t10 = $[17];
      }
      let t11;
      if ($[18] !== tag) {
        t11 = tag ? <div className={styles.tagOverlay}>{tag}</div> : null;
        $[18] = tag;
        $[19] = t11;
      } else {
        t11 = $[19];
      }
      let t12;
      if ($[20] !== tAria) {
        t12 = (image, index) => <button key={galleryImageKey(image, index)} type="button" className={styles.slide} onClick={openOverlay} aria-label={tAria("image_gallery_show_all")}>{renderGalleryImage(image, styles.slideImage, index === 0 ? "eager" : "lazy")}</button>;
        $[20] = tAria;
        $[21] = t12;
      } else {
        t12 = $[21];
      }
      t4 = <section className={styles.slider} aria-roledescription={t9} aria-label={t10}>{t11}<div ref={scrollRef} className={styles.scrollTrack} onScroll={handleScroll}>{resolved.map(t12)}</div>{resolved.length > 1 && <ul className={styles.dots} aria-label={t("itinerary_gallery_slides")}>{resolved.map((image_0, i) => <li key={galleryImageKey(image_0, i)}><span className={clsx(styles.dot, i === slideIndex && styles.dotActive)} aria-current={i === slideIndex ? "true" : undefined} /></li>)}</ul>}</section>;
      t5 = !sliderOnly ? <div className={styles.desktopGallery}><button type="button" className={styles.mainImageWrapper} onClick={openOverlay} aria-label={tAria("image_gallery_show_all")} data-test-id="ItineraryCardGalleryOpen">{tag ? <div className={styles.tagOverlay}>{tag}</div> : null}{isDatoImage(mainImage) ? <Image data={mainImage.data} className={styles.mainImage} layout="fill" objectFit="cover" sizes={MAIN_IMAGE_SIZES} /> : <img src={mainImage.src} srcSet={mainImage.srcSet} sizes={mainImage.srcSet ? MAIN_IMAGE_SIZES : undefined} alt={mainImage.alt ?? ""} className={styles.mainImage} loading="lazy" />}{resolved.length > 1 && <span className={styles.badge} aria-hidden="true"><BoundingBox size="small" decorative={true} /><span>{resolved.length}</span></span>}</button>{second && third && <div className={styles.subImages}><button type="button" className={styles.subImageWrapper} onClick={openOverlay} aria-label={tAria("image_gallery_show_all")}>{isDatoImage(second) ? <Image data={second.data} className={styles.subImage} layout="fill" objectFit="cover" sizes={SUB_IMAGE_SIZES} /> : <img src={second.src} srcSet={second.srcSet} sizes={second.srcSet ? SUB_IMAGE_SIZES : undefined} alt={second.alt ?? ""} className={styles.subImage} loading="lazy" />}</button><button type="button" className={styles.subImageWrapper} onClick={openOverlay} aria-label={tAria("image_gallery_show_all")}>{isDatoImage(third) ? <Image data={third.data} className={styles.subImage} layout="fill" objectFit="cover" sizes={SUB_IMAGE_SIZES} /> : <img src={third.src} srcSet={third.srcSet} sizes={third.srcSet ? SUB_IMAGE_SIZES : undefined} alt={third.alt ?? ""} className={styles.subImage} loading="lazy" />}</button></div>}</div> : null;
    }
    $[0] = images;
    $[1] = slideIndex;
    $[2] = sliderOnly;
    $[3] = t;
    $[4] = tAria;
    $[5] = tag;
    $[6] = resolved;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    resolved = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsOverlayOpen(false);
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== isOverlayOpen || $[24] !== resolved) {
    t8 = <ImageGalleryCarouselModal images={resolved} isOpen={isOverlayOpen} onClose={t7} closeDataTestId="ItineraryCardGalleryClose" dataTestId="ItineraryCardGalleryOverlay" />;
    $[23] = isOverlayOpen;
    $[24] = resolved;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t2 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t8) {
    t9 = <div data-test-id={t2} className={t3}>{t4}{t5}{t8}</div>;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}